import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useLive } from "../live/channel";
import { useActivities, useQuestions } from "../live/control";
import { QueueRow } from "../live/Queue";
import {
  ACTIVITY_PARAM,
  activityFromParam,
  BUCKETS,
  countByBucket,
  filterAnswers,
  offeredActivity,
  ofActivity,
  questionActivities,
  withLiveQuestions,
  type Bucket,
} from "../live/moderation";
import { withContext } from "./registry";

/**
 * ═══ LA MODÉRATION DES QUESTIONS, SUR SON PROPRE ÉCRAN ═══
 *
 * ⚠️ ELLE VIVAIT DANS LA RÉGIE, en bas, sous le pilotage. Une plénière de huit cents
 * personnes empile des centaines de questions : la liste poussait les commandes hors
 * de l'écran, et modérer voulait dire perdre de vue ce que la salle regardait. Deux
 * écrans, donc, et on peut les tenir sur deux appareils — celui qui modère n'est
 * souvent pas celui qui pilote.
 *
 * ⚠️ IL MONTRE LES QUESTIONS DE TOUTES LES ACTIVITÉS, filtrables par activité (#341).
 * Il ne modérait d'abord que celle à l'écran, parce que la charge du direct ne porte
 * qu'elle : une fois la session suivante lancée, les questions de la précédente ne
 * se relisaient plus, et celui qui préparait les questions de l'après-midi ne les
 * voyait qu'en les projetant. Elles ont désormais leur propre route
 * (`useQuestions`) ; celles de l'activité à l'écran continuent d'arriver par le
 * direct, à la seconde (`withLiveQuestions`).
 *
 * ⚠️ « METTRE À L'ÉCRAN » SE PROPOSE SUR TOUTES (#349). Il était réservé à l'activité
 * à l'écran, puisque la salle ne lit que la question de celle qu'elle montre ; le
 * serveur y bascule maintenant la salle dans le même geste.
 *
 * Displayed strings are French: this is a French product.
 */
export function LiveQuestions() {
  const { data: live, isPending } = useLive();
  /* Le préfixe d'événement de l'adresse, comme partout ailleurs : un lien qui
     l'oublie sort le visiteur de son propre événement. */
  const { context } = useParams();
  const [bucket, setBucket] = useState<Bucket>("received");
  const [search, setSearch] = useState("");
  /* L'activité demandée par l'adresse : la régie en ouvre une depuis sa ligne (#451).
     Une valeur de départ, pas un réglage : le sélecteur reste seul maître ensuite. */
  const [params] = useSearchParams();
  const [activityId, setActivityId] = useState<number | null>(() =>
    activityFromParam(params.get(ACTIVITY_PARAM)),
  );
  const questions = useQuestions();
  /* Les titres, et l'ordre de la régie. Une lecture ratée n'empêche pas de modérer :
     les questions s'affichent, sans le nom de leur activité ni le filtre. */
  const { data: activities } = useActivities();

  /* Le filtre ramené aux activités qui existent encore : voir `offeredActivity`. Et
     l'état suit la vue, comme le type dans la régie.
     ⚠️ UNE FOIS LA LISTE LUE SEULEMENT. Avant, elle est vide : le filtre venu de
     l'adresse était ramené à « Toutes » au premier rendu, avant même que l'activité
     demandée ait pu arriver (#451). */
  const offered = questionActivities(activities ?? []);
  const shownActivity = offeredActivity(activityId, offered);
  useEffect(() => {
    if (activities && activityId !== shownActivity) setActivityId(shownActivity);
  }, [activities, activityId, shownActivity]);

  if (isPending || (live?.role === "control" && questions.isPending)) {
    return (
      <section className="page">
        <p className="empty">Chargement des questions.</p>
      </section>
    );
  }

  /* ⚠️ UNE LECTURE RATÉE N'EST PAS UNE AUTORISATION : la même garde que la régie, et
     pour la même raison — une console d'apparence normale dont chaque geste sera
     refusé se découvre au pire moment. */
  if (!live || (live.role === "control" && !questions.data)) {
    return (
      <section className="page">
        <p className="empty">
          Les questions n'ont pas pu être chargées. Vérifiez votre connexion, la
          page réessaie toute seule.
        </p>
      </section>
    );
  }

  if (live.role !== "control") {
    return (
      <section className="page">
        <p className="empty">
          Cet écran est réservé à l'organisation de l'événement.
        </p>
      </section>
    );
  }

  /* ⚠️ L'ACTIVITÉ AVANT LES PILES : les comptes des onglets doivent décrire ce qu'on
     verrait en cliquant dessus, pas toutes les activités confondues. */
  const answers = ofActivity(withLiveQuestions(questions.data ?? [], live), shownActivity);
  const counts = countByBucket(answers);
  const shown = filterAnswers(answers, bucket, search);
  /* L'activité à l'écran : mettre au mur une question d'une autre y bascule la salle. */
  const onScreen = live.activity?.type === "questions" ? live.activity.id : null;
  const titleOf = new Map(offered.map((a) => [a.id, a.title]));
  /* Le nom de l'activité sur chaque ligne, quand la liste en mélange plusieurs. */
  const mixed = shownActivity === null && titleOf.size > 1;

  return (
    <section className="page rgq-page">
      <div className="ttl">
        <h2>Questions du public</h2>
        <p>
          Ce que vous validez ici part sur les téléphones ; ce que vous mettez à
          l'écran s'affiche en salle.
        </p>
      </div>

      {/* ⚠️ LE RETOUR EST DIT, parce que cet écran n'est pas dans la navigation d'un
          participant et qu'on y arrive depuis la régie. Sans lui, on en sort par le
          bouton « précédent » du navigateur, ou pas du tout. */}
      <p className="hint">
        <Link to={withContext("/live/regie", context)}>Retour à la régie</Link>
      </p>

      {activities && !offered.length ? (
        /* ⚠️ DIRE POURQUOI C'EST VIDE. Sans activité « questions », personne n'a pu
           en poser : une page nue se lit comme une panne, au moment où l'on cherche
           justement les questions. Affirmé seulement si la liste a été LUE — une
           lecture ratée ne dit rien de ce que l'événement a préparé. */
        <p className="empty">
          Aucune activité « Questions du public » n'est préparée. Créez-en une
          depuis la régie : les questions arriveront ici.
        </p>
      ) : (
        <>
          <div className="rgq-page__f">
            <div className="rg__seg" role="group" aria-label="Questions affichées">
              {BUCKETS.map((b) => (
                <button
                  key={b.key}
                  type="button"
                  aria-pressed={bucket === b.key}
                  onClick={() => setBucket(b.key)}
                >
                  {b.label} ({counts[b.key]})
                </button>
              ))}
            </div>

            {/* ⚠️ UNE RECHERCHE, ET C'EST TOUTE LA RAISON DE CET ÉCRAN. Retrouver une
                question parmi trois cents ne se fait pas en défilant. */}
            <label className="rgq-page__s">
              <span className="a11y">Rechercher dans les questions</span>
              <input
                type="search"
                value={search}
                placeholder="Rechercher"
                autoComplete="off"
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>

            {/* ⚠️ SEULEMENT À PARTIR DE DEUX ACTIVITÉS : avec une seule, « toutes »
                et « celle-là » sont la même liste, et le sélecteur ne filtre rien. */}
            {offered.length > 1 ? (
              <label className="rg__type">
                Activité
                <select
                  value={shownActivity ?? ""}
                  onChange={(e) =>
                    setActivityId(e.target.value ? Number(e.target.value) : null)
                  }
                >
                  <option value="">Toutes</option>
                  {offered.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.id === onScreen ? `${a.title} · à l'écran` : a.title}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>

          {shown.length ? (
            <div className="clist">
              {shown.map((answer) => (
                <QueueRow
                  key={answer.id}
                  answer={answer}
                  onScreen={
                    answer.activityId === onScreen && live.spotlight?.id === answer.id
                  }
                  elsewhere={answer.activityId !== onScreen}
                  activity={mixed ? titleOf.get(answer.activityId) : undefined}
                />
              ))}
            </div>
          ) : (
            /* « Vide ici » n'est pas « vide » : une recherche sans résultat et une
               pile sans question ne se disent pas pareil. */
            <p className="empty">
              {search
                ? `Aucune question ne contient « ${search} » dans cette pile.`
                : "Aucune question dans cette pile pour l'instant."}
            </p>
          )}
        </>
      )}
    </section>
  );
}
